import React, { FC, FunctionComponent } from 'react';
import { Link, useLanguage } from '@/contexts/LanguageContext';
import { Icon } from '../Icon';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { useNavigationConfig } from '@/hooks/useNavigationConfig';
import { HeaderTopItemMobile } from './TopItemMobile';
import { JCDigitalNotice } from '@/components/ui/Info/JCDigitalNotice';

export type BreadCrumbsProps = {
  breadCrumbs: Array<{
    url: string;
    text: string;
  }>;
  className?: string;
};

const BreadCrumbs: FC<BreadCrumbsProps> = ({ breadCrumbs, className }) => {
  const { t } = useLanguage();
  const additionalCrumbs = [{ url: '/[lang]', text: t.general.navigation.links.home }];
  return (
    <div className={className}>
      <div className="flex flex-wrap items-center">
        {[...additionalCrumbs, ...breadCrumbs].map(({ url, text }, index, arr) => {
          const isLast = index === arr.length - 1;
          return (
            <React.Fragment key={url + text}>
              {!isLast && (
                <>
                  <Link href={url}>
                    <a className="hover:underline">{text}</a>
                  </Link>
                  <Icon name="Right Arrow" className="w-2 mx-2 opacity-75" />
                </>
              )}
              {isLast && <span className="font-semibold">{text}</span>}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

type HeaderProps = {
  breadCrumbs?: BreadCrumbsProps['breadCrumbs'];
  showLogoOnDesktop: boolean;
  showBackButton?: boolean;
  overrideBackAction?: () => void;
  className?: string;
};

export const Header: FunctionComponent<HeaderProps> = React.memo(
  ({ breadCrumbs, showBackButton, className, showLogoOnDesktop, overrideBackAction }) => {
    const router = useRouter();
    const { navigationConfig } = useNavigationConfig();
    const topItemMobile = navigationConfig?.topItemMobile;

    return (
      <>
        <div className={cn('relative flex justify-end h-8 max-w-4xl lg:justify-between lg:pr-10 lg:h-12', className)}>
          {breadCrumbs && (
            <div>
              <BreadCrumbs breadCrumbs={breadCrumbs} className="hidden lg:block" />
            </div>
          )}
          {showBackButton && (
            <>
              <button
                className="flex items-center mr-auto lg:self-start"
                onClick={() => {
                  if (overrideBackAction) {
                    overrideBackAction();
                  } else {
                    router.back();
                  }
                }}
              >
                <Icon name="Left Arrow" className="w-3 mr-2" />
                <span className="mt-px">Zurück</span>
              </button>
            </>
          )}
          <div
            className={cn('hidden ', {
              'lg:block h-full': showLogoOnDesktop,
            })}
          >
            <Link href="[lang]/">
              <a className="h-full pointer-events-auto">
                <img src="/static/Jobcenter Logo.svg" className="h-full" alt="Jobcenter Logo" />
              </a>
            </Link>
          </div>
          <div className="absolute inset-0 flex justify-center pointer-events-none lg:hidden">
            <Link href="[lang]/">
              <a className="h-full pointer-events-auto">
                <img src="/static/Jobcenter Logo.svg" className="h-full" alt="Jobcenter Logo" />
              </a>
            </Link>
          </div>

          {topItemMobile && <HeaderTopItemMobile {...topItemMobile} />}
        </div>
        {breadCrumbs && <BreadCrumbs breadCrumbs={breadCrumbs} className="mt-12 lg:hidden" />}
        <JCDigitalNotice />
      </>
    );
  }
);
